import React from 'react'

interface SkeletonLoaderProps {
  className?: string
  variant?: 'text' | 'card' | 'button' | 'avatar' | 'price'
  lines?: number
  width?: string
  height?: string
}

export const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  className = '',
  variant = 'text',
  lines = 1,
  width,
  height
}) => {
  const baseClasses = 'animate-pulse bg-gray-200 rounded'

  const variantClasses = {
    text: 'h-4',
    card: 'h-32',
    button: 'h-10',
    avatar: 'h-12 w-12 rounded-full',
    price: 'h-8'
  }

  const getSkeletonClasses = () => {
    let classes = `${baseClasses} ${variantClasses[variant]} ${className}`

    if (width) classes += ` w-${width}`
    if (height) classes += ` h-${height}`

    return classes
  }

  if (variant === 'text' && lines > 1) {
    return (
      <div className="space-y-2">
        {Array.from({ length: lines }).map((_, index) => (
          <div
            key={index}
            className={getSkeletonClasses()}
            style={{
              width: index === lines - 1 ? '75%' : '100%'
            }}
          />
        ))}
      </div>
    )
  }

  return <div className={getSkeletonClasses()} style={{ width, height }} />
}

// Specialized skeleton components for common use cases

export const CreditBalanceSkeleton: React.FC = () => (
  <div className="bg-white rounded-lg border p-6">
    <div className="flex items-center justify-between mb-4">
      <SkeletonLoader variant="text" width="32" />
      <SkeletonLoader variant="avatar" />
    </div>
    <SkeletonLoader variant="price" width="24" className="mb-2" />
    <SkeletonLoader variant="text" lines={2} />
    <div className="mt-4">
      <SkeletonLoader variant="button" width="full" />
    </div>
  </div>
)

export const SubscriptionCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-lg border p-6">
    <div className="flex items-center justify-between mb-4">
      <SkeletonLoader variant="text" width="40" />
      <SkeletonLoader variant="text" width="20" />
    </div>
    <SkeletonLoader variant="text" lines={3} className="mb-4" />
    <div className="grid grid-cols-2 gap-4 mb-6">
      <div>
        <SkeletonLoader variant="text" width="16" className="mb-1" />
        <SkeletonLoader variant="price" width="20" />
      </div>
      <div>
        <SkeletonLoader variant="text" width="16" className="mb-1" />
        <SkeletonLoader variant="text" width="24" />
      </div>
    </div>
    <SkeletonLoader variant="button" width="full" />
  </div>
)

export const PricingPlanSkeleton: React.FC = () => (
  <div className="bg-white rounded-lg border p-6 relative">
    <div className="text-center mb-6">
      <SkeletonLoader variant="text" width="32" className="mx-auto mb-2" />
      <SkeletonLoader variant="price" width="24" className="mx-auto mb-1" />
      <SkeletonLoader variant="text" width="20" className="mx-auto" />
    </div>

    <div className="space-y-3 mb-6">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="flex items-center">
          <SkeletonLoader variant="text" width="4" height="4" className="rounded-full mr-3 flex-shrink-0" />
          <SkeletonLoader variant="text" className="flex-1" />
        </div>
      ))}
    </div>

    <SkeletonLoader variant="button" width="full" height="12" />
  </div>
)

export const BillingHistorySkeleton: React.FC = () => (
  <div className="bg-white rounded-lg border">
    <div className="p-6 border-b">
      <SkeletonLoader variant="text" width="40" className="mb-2" />
      <SkeletonLoader variant="text" width="64" />
    </div>
    <div className="divide-y">
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="p-4 flex items-center justify-between">
          <div className="flex-1">
            <SkeletonLoader variant="text" width="48" className="mb-1" />
            <SkeletonLoader variant="text" width="32" />
          </div>
          <div className="text-right">
            <SkeletonLoader variant="text" width="20" className="mb-1" />
            <SkeletonLoader variant="text" width="16" />
          </div>
        </div>
      ))}
    </div>
  </div>
)

export default SkeletonLoader